import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  IdCard,
  CalendarClock,
  GraduationCap,
  CircleUserRound,
  Users,
  ShieldCheck,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { User } from "../types";
import { getPendingClassesForUser } from "../api/Users";
import "./Sidebar.css";

interface SidebarProps {
  user: User;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  badge?: number;
}

const COLLAPSE_STORAGE_KEY = "cec-sidebar-collapsed";

const Sidebar = ({ user }: SidebarProps) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    const fetchPendingCount = async () => {
      try {
        const res = await getPendingClassesForUser(user.id);
        if (!cancelled) setPendingCount(res.items?.length || 0);
      } catch (err) {
        console.error("Sidebar: failed to fetch pending count:", err);
      }
    };
    fetchPendingCount();
    return () => { cancelled = true; };
  }, [user.id]);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // localStorage unavailable (private browsing, etc.) — collapse still
        // works for this session, just won't persist across reloads.
      }
      return next;
    });
  };

  const items: NavItem[] = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, badge: pendingCount },
    { to: "/my-licenses", label: "My Licenses", icon: IdCard },
    { to: "/cycles", label: "Cycles", icon: CalendarClock },
    { to: "/classes", label: "Classes", icon: GraduationCap },
    { to: "/profile", label: "Profile", icon: CircleUserRound },
  ];

  if (user.subscription?.plan === "team" || user.isTeamAdmin || user.role === "admin") {
    items.push({ to: "/team", label: "Team", icon: Users });
  }
  if (user.role === "admin") {
    items.push({ to: "/admin", label: "Admin", icon: ShieldCheck });
  }

  return (
    <aside className={`cec-sidebar${collapsed ? " cec-sidebar-collapsed" : ""}`} aria-label="Main navigation">
      <nav className="cec-sidebar-nav">
        <ul className="cec-sidebar-list">
          {items.map(({ to, label, icon: Icon, badge }) => (
            <li key={to}>
              <NavLink
                to={to}
                className={({ isActive }) => `cec-sidebar-link${isActive ? " active" : ""}`}
                title={collapsed ? label : undefined}
              >
                <span className="cec-sidebar-icon">
                  <Icon size={20} strokeWidth={1.75} />
                  {!!badge && (
                    <span className="cec-sidebar-badge-dot" aria-hidden="true" />
                  )}
                </span>
                <span className="cec-sidebar-label">{label}</span>
                {!!badge && !collapsed && (
                  <span className="cec-sidebar-badge">{badge}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <button
        type="button"
        className="cec-sidebar-collapse-btn"
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <PanelLeftOpen size={18} strokeWidth={1.75} /> : <PanelLeftClose size={18} strokeWidth={1.75} />}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
};

export default Sidebar;
